import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { SOPSummaryStats } from '../../types/sopSummary';
import { sopService } from '../../services/firebase/SOPService';
import { sopSummaryService } from '../../services/sopSummaryService';
import { useToast } from '../../components/ui/use-toast';

// Interface for the result returned by generateSOPSummaries
interface GenerateSummariesResult {
  success: boolean;
  message: string;
  results?: {
    total: number;
    completed: number;
    failed: number;
    skipped: number;
    details: Array<{
      id: string;
      title: string;
      status: 'completed' | 'failed' | 'skipped';
      reason?: string;
      shortSummary?: string;
      detailedSummary?: string;
      keywords?: string[];
      error?: string;
    }>;
  };
  stats?: SOPSummaryStats;
  lastProcessedIndex?: number;
  remainingCount?: number;
  partialBatch?: boolean;
}

/**
 * Admin section for managing SOP summaries
 */
const SOPSummarySection: React.FC = () => {
  const { toast } = useToast();
  const [batchSize, setBatchSize] = useState<number>(25); // Default to 25 for optimal performance
  const [startIndex, setStartIndex] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [stats, setStats] = useState<SOPSummaryStats | null>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalToProcess, setTotalToProcess] = useState<number>(100);
  const [currentBatch, setCurrentBatch] = useState<number>(1);
  const [totalBatches, setTotalBatches] = useState<number>(1);
  const [batchResults, setBatchResults] = useState<any[]>([]);
  const [onlyMissingSummaries, setOnlyMissingSummaries] = useState<boolean>(false);
  const [missingSummariesCount, setMissingSummariesCount] = useState<number>(0);
  
  // Adaptive batch size management
  const [recommendedBatchSize, setRecommendedBatchSize] = useState<number>(25);
  const [useAdaptiveBatchSize, setUseAdaptiveBatchSize] = useState<boolean>(true);
  
  // Resume functionality
  const [canResume, setCanResume] = useState<boolean>(false);
  const [resumeData, setResumeData] = useState<any>(null);

  const [totalSOPs, setTotalSOPs] = useState<number>(0);

  // Fetch summary statistics on mount
  useEffect(() => {
    fetchStats();
    
    // Check for saved progress on component mount
    const savedProgress = localStorage.getItem('sopSummaryProgress');
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        setResumeData(progress);
        setCanResume(true);
      } catch (e) {
        console.error('Error parsing saved progress:', e);
      }
    }
  }, []);
  
  // Calculate optimal batch size based on processing history
  useEffect(() => {
    if (stats?.averageProcessingTime && useAdaptiveBatchSize) {
      const optimal = calculateOptimalBatchSize(stats.averageProcessingTime);
      setRecommendedBatchSize(optimal);
      setBatchSize(optimal);
    }
  }, [stats, useAdaptiveBatchSize]);
  
  // Calculate optimal batch size based on average processing time
  const calculateOptimalBatchSize = (avgTimePerSOP: number) => {
    // Target: 8 minutes (480 seconds) max processing time per batch
    // Leave 1 minute buffer for safety
    const maxTimePerBatch = 480 * 1000; // 8 minutes in ms
    
    // Calculate how many SOPs can be processed in the available time
    const optimalBatchSize = Math.floor(maxTimePerBatch / avgTimePerSOP);
    
    // Ensure batch size is between 5 and 50
    return Math.max(5, Math.min(50, optimalBatchSize));
  };

  // Fetch summary statistics
  const fetchStats = async () => {
    try {
      const [stats, metrics] = await Promise.all([
        sopSummaryService.getSummaryStats(),
        sopService.getSOPMetrics()
      ]);
      
      setStats(stats);
      setTotalSOPs(metrics.totalSOPs);
      
      // Calculate the number of SOPs without summaries
      const missingSummaries = metrics.totalSOPs - stats.completed;
      setMissingSummariesCount(missingSummaries);
    } catch (error) {
      console.error('Error fetching summary stats:', error);
      setError('Failed to fetch summary statistics');
    }
  };

  // Handle the generate summaries button click
  const handleGenerateSummaries = async () => {
    // Determine if we need to process multiple batches
    const needsMultipleBatches = totalToProcess > batchSize;
    
    if (needsMultipleBatches) {
      await processMultipleBatches();
    } else {
      await generateSingleBatch();
    }
  };

  // Generate summaries for a single batch
  const generateSingleBatch = async () => {
    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const result: GenerateSummariesResult = await sopService.generateSOPSummaries({
        batchSize: totalToProcess, // Use totalToProcess as the batch size for single batch
        startIndex,
        testMode: true, // Always use test mode to limit to specified size
        onlyMissing: onlyMissingSummaries // Pass the onlyMissing parameter
      });

      if (result.success) {
        // Check if this was a partial batch (timeout occurred)
        if (result.partialBatch) {
          console.log(`Partial batch detected. Last processed index: ${result.lastProcessedIndex || 0}, remaining: ${result.remainingCount || 0}`);
          
          // Store progress for potential resume
          localStorage.setItem('sopSummaryProgress', JSON.stringify({
            startIndex: (result.lastProcessedIndex || 0) + 1,
            remaining: result.remainingCount || 0
          }));
          
          setCanResume(true);
          setResumeData({
            startIndex: (result.lastProcessedIndex || 0) + 1,
            remaining: result.remainingCount || 0
          });
          
          toast({
            title: 'Partial Processing',
            description: 'Processing was interrupted due to timeout. You can resume from where it left off.',
            variant: 'destructive',
            duration: 5000
          });
        }
        toast({
          title: 'Success',
          description: result.message,
          duration: 5000
        });
        setResult(result);
        fetchStats(); // Refresh stats
      } else {
        setError(result.message);
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error generating summaries:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Resume function
  const handleResume = () => {
    if (resumeData) {
      setStartIndex(resumeData.startIndex);
      setTotalToProcess(resumeData.remaining);
      
      // Clear the saved progress
      localStorage.removeItem('sopSummaryProgress');
      setCanResume(false);
      setResumeData(null);
      
      // Start processing
      handleGenerateSummaries();
    }
  };

  // Process multiple batches sequentially
  const processMultipleBatches = async () => {
    setIsGenerating(true);
    setError(null);
    setBatchResults([]);
    
    // Calculate total number of batches
    const numBatches = Math.ceil(totalToProcess / batchSize);
    setTotalBatches(numBatches);
    setCurrentBatch(1);
    
    let currentStartIndex = startIndex;
    let remainingToProcess = totalToProcess;
    let allCompleted = 0;
    let allFailed = 0;
    let allSkipped = 0;
    
    try {
      for (let i = 0; i < numBatches && remainingToProcess > 0; i++) {
        setCurrentBatch(i + 1);
        
        // Calculate current batch size
        const currentBatchSize = Math.min(batchSize, remainingToProcess);
        
        console.log(`Processing batch ${i + 1} of ${numBatches}: ${currentBatchSize} SOPs starting at index ${currentStartIndex}`);
        
        // Process this batch
        const result: GenerateSummariesResult = await sopService.generateSOPSummaries({
          batchSize: currentBatchSize,
          startIndex: currentStartIndex,
          testMode: true, // Always use test mode to limit to specified size
          onlyMissing: onlyMissingSummaries // Pass the onlyMissing parameter
        });
        
        // Update batch results
        setBatchResults(prev => [...prev, result]);
        
        if (result.success) {
          // Create default results object if undefined
          const resultData = result.results || { total: 0, completed: 0, failed: 0, skipped: 0, details: [] };
          
          // Update counters
          allCompleted += resultData.completed || 0;
          allFailed += resultData.failed || 0;
          allSkipped += resultData.skipped || 0;
          
          // Show toast for this batch
          toast({
            title: `Batch ${i + 1} of ${numBatches} completed`,
            description: `Processed ${resultData.total || 0} SOPs: ${resultData.completed || 0} completed, ${resultData.failed || 0} failed, ${resultData.skipped || 0} skipped`,
            duration: 3000
          });
          
          // Check if this was a partial batch (timeout occurred)
          if (result.partialBatch) {
            console.log(`Partial batch detected. Last processed index: ${result.lastProcessedIndex || 0}, remaining: ${result.remainingCount || 0}`);
            
            // Update for next batch
            currentStartIndex = (result.lastProcessedIndex || 0) + 1;
            remainingToProcess = result.remainingCount || 0;
            
            // Store progress for potential resume
            localStorage.setItem('sopSummaryProgress', JSON.stringify({
              startIndex: currentStartIndex,
              remaining: remainingToProcess,
              completed: allCompleted,
              failed: allFailed,
              skipped: allSkipped
            }));
          } else {
            // Normal batch completion
            currentStartIndex += currentBatchSize;
            remainingToProcess -= currentBatchSize;
          }
          
          // Add a delay between batches to avoid overloading
          if (i < numBatches - 1 && remainingToProcess > 0) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } else {
          // Stop processing if a batch fails
          setError(`Batch ${i + 1} failed: ${result.message}`);
          toast({
            title: 'Error',
            description: `Batch ${i + 1} failed: ${result.message}`,
            variant: 'destructive',
            duration: 5000
          });
          
          // Store progress for potential resume
          localStorage.setItem('sopSummaryProgress', JSON.stringify({
            startIndex: currentStartIndex,
            remaining: remainingToProcess,
            completed: allCompleted,
            failed: allFailed,
            skipped: allSkipped
          }));
          
          setCanResume(true);
          setResumeData({
            startIndex: currentStartIndex,
            remaining: remainingToProcess
          });
          
          break;
        }
      }
      
      // Show final results
      const finalResult = {
        success: true,
        message: `Processed ${totalToProcess} SOPs across ${numBatches} batches: ${allCompleted} completed, ${allFailed} failed, ${allSkipped} skipped`,
        results: {
          total: totalToProcess,
          completed: allCompleted,
          failed: allFailed,
          skipped: allSkipped
        }
      };
      
      setResult(finalResult);
      toast({
        title: 'All batches completed',
        description: finalResult.message,
        duration: 5000
      });
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      console.error('Error processing multiple batches:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Determine if we need to show the batch progress indicator
  const isMultiBatchProcessing = totalToProcess > batchSize && isGenerating;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>SOP Summaries</CardTitle>
        <CardDescription>
          Generate AI-powered summaries for SOP documents
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary Statistics */}
        {stats && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Summary Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Total</div>
                <div className="text-2xl font-bold">{stats.total}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Completed</div>
                <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Pending</div>
                <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Error</div>
                <div className="text-2xl font-bold text-red-600">{stats.error}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Permission Denied</div>
                <div className="text-2xl font-bold text-orange-600">{stats.permissionDenied}</div>
              </div>
            </div>
            
            {/* Additional Stats - Total SOPs and SOPs with AI Summary */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Total SOPs</div>
                <div className="text-2xl font-bold">{totalSOPs}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">SOPs with AI Summary</div>
                <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="text-sm font-medium">
                  {totalSOPs > 0 ? Math.round((stats.completed / totalSOPs) * 100) : 0}%
                </span>
              </div>
              <Progress 
                value={totalSOPs > 0 ? (stats.completed / totalSOPs) * 100 : 0} 
                className="h-2"
              />
            </div>
            
            {/* Additional Stats */}
            {stats.averageProcessingTime && (
              <div className="text-sm text-muted-foreground mb-1">
                Average Processing Time: {Math.round(stats.averageProcessingTime / 1000)} seconds
              </div>
            )}
            {stats.averageDocLength && (
              <div className="text-sm text-muted-foreground">
                Average Document Length: {Math.round(stats.averageDocLength).toLocaleString()} words
              </div>
            )}
          </div>
        )}
        
        <Separator className="my-6" />
        
        {/* Generation Controls */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Generate Summaries</h3>
          
          {/* Only Missing Summaries Checkbox */}
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="checkbox"
              id="onlyMissingSummaries"
              checked={onlyMissingSummaries}
              onChange={(e) => setOnlyMissingSummaries(e.target.checked)}
              disabled={isGenerating}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <Label htmlFor="onlyMissingSummaries">
              Only SOPs without summaries ({missingSummariesCount} found)
            </Label>
          </div>
          
          {/* Adaptive Batch Size Checkbox */}
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="checkbox"
              id="useAdaptiveBatchSize"
              checked={useAdaptiveBatchSize}
              onChange={(e) => setUseAdaptiveBatchSize(e.target.checked)}
              disabled={isGenerating || !stats?.averageProcessingTime}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <Label htmlFor="useAdaptiveBatchSize">
              Use recommended batch size ({recommendedBatchSize} SOPs)
            </Label>
          </div>
          
          {/* Resume Processing Alert */}
          {canResume && (
            <Alert className="mb-4">
              <AlertTitle>Resume Available</AlertTitle>
              <AlertDescription>
                You can resume processing from where you left off.
              </AlertDescription>
              <Button
                onClick={handleResume}
                className="mt-2"
                variant="outline"
                disabled={isGenerating}
              >
                Resume Processing
              </Button>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="batchSize">Batch Size</Label>
              <Input
                id="batchSize"
                type="number"
                min={1}
                max={100}
                value={batchSize}
                onChange={(e) => setBatchSize(parseInt(e.target.value) || 25)}
                disabled={isGenerating}
              />
              <p className="text-sm text-muted-foreground">
                Recommended: {recommendedBatchSize} SOPs per batch for optimal performance
              </p>
              {batchSize > recommendedBatchSize && stats?.averageProcessingTime && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTitle>Warning</AlertTitle>
                  <AlertDescription>
                    The selected batch size ({batchSize}) is larger than the recommended size ({recommendedBatchSize})
                    based on average processing time. This may cause timeouts.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="startIndex">Start Index</Label>
              <Input
                id="startIndex"
                type="number"
                min={0}
                value={startIndex}
                onChange={(e) => setStartIndex(parseInt(e.target.value) || 0)}
                disabled={isGenerating}
              />
              <p className="text-sm text-muted-foreground">
                Index to start processing from (0-based)
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="totalToProcess">Total SOPs to Process</Label>
              <Input
                id="totalToProcess"
                type="number"
                min={1}
                value={totalToProcess}
                onChange={(e) => setTotalToProcess(parseInt(e.target.value) || 100)}
                disabled={isGenerating}
              />
              <p className="text-sm text-muted-foreground">
                {totalToProcess > batchSize 
                  ? `Will process in ${Math.ceil(totalToProcess / batchSize)} batches of ${batchSize} SOPs each`
                  : 'Will process in a single batch'}
              </p>
            </div>
          </div>
          
          {/* Batch Progress Indicator */}
          {isMultiBatchProcessing && (
            <div className="mt-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm text-muted-foreground">Overall Progress</span>
                <span className="text-sm font-medium">
                  Batch {currentBatch} of {totalBatches} ({Math.round((currentBatch / totalBatches) * 100)}%)
                </span>
              </div>
              <Progress 
                value={(currentBatch / totalBatches) * 100} 
                className="h-2"
              />
            </div>
          )}
          
          <div className="mt-4">
            <Button 
              onClick={handleGenerateSummaries} 
              disabled={isGenerating}
              className="w-full md:w-auto"
            >
              {isGenerating ? 'Generating...' : 'Generate Summaries'}
            </Button>
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mt-6">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Results */}
        {result && result.results && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold">Results</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Total</div>
                <div className="text-2xl font-bold">{result.results.total}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Completed</div>
                <div className="text-2xl font-bold text-green-600">{result.results.completed}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Skipped</div>
                <div className="text-2xl font-bold text-blue-600">{result.results.skipped}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-sm text-muted-foreground">Failed</div>
                <div className="text-2xl font-bold text-red-600">{result.results.failed}</div>
              </div>
            </div>
            
            {/* Details */}
            {result.results.details && result.results.details.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Details</h4>
                <div className="max-h-60 overflow-y-auto border rounded-md p-2">
                  {result.results.details.map((detail: any, index: number) => (
                    <div key={index} className="mb-2 p-2 border-b last:border-b-0">
                      <div className="flex justify-between">
                        <span className="font-medium">{detail.title}</span>
                        <span className={`text-sm ${
                          detail.status === 'completed' ? 'text-green-600' : 
                          detail.status === 'skipped' ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {detail.status}
                        </span>
                      </div>
                      {detail.reason && (
                        <div className="text-sm text-muted-foreground">{detail.reason}</div>
                      )}
                      {detail.error && (
                        <div className="text-sm text-red-600">{detail.error}</div>
                      )}
                      {detail.summary && (
                        <div className="text-sm mt-1">{detail.summary}</div>
                      )}
                      {detail.keywords && detail.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {detail.keywords.map((keyword: string, kidx: number) => (
                            <span key={kidx} className="text-xs bg-muted px-2 py-1 rounded-full">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SOPSummarySection;
